import { useEffect, useRef, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { AgGridReact } from 'ag-grid-react';
import CurrencyInput from 'react-currency-input-field';
import PropTypes from 'prop-types';
import { Stack, Box, Dialog, DialogContent, CircularProgress, TextField, InputAdornment, Tooltip, IconButton, Checkbox, Typography, Button } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import Iconify from '../../reuseable-components/iconify/Iconify';
import OrderGuideUpdateByPercenatge from './OrderGuideUpdateByPercenatge';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';
import ConfirmDialog from '../../reuseable-components/ConfirmDialog/ConfirmDialog';

OrderGuideProductsForm.propTypes = {
  editOrderGuide: PropTypes.object,
  setOrderGuides: PropTypes.func,
  products: PropTypes.array,
  purchasingGroups: PropTypes.array,
  productCategories: PropTypes.array,
  facilities: PropTypes.array,
  loadingProducts: PropTypes.bool,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function OrderGuideProductsForm({ editOrderGuide, setOrderGuides, products, loadingProducts, purchasingGroups, productCategories, facilities, open, close }) {
  const gridRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingOrderGuideProducts, setLoadingOrderGuideProducts] = useState(false);
  const [showPriceConfirmWarning, setShowPriceConfirmWarning] = useState(false);
  const [originalOrderGuide, setOriginalOrderGuide] = useState([]);
  const [localProducts, setLocalProducts] = useState([]);
  const [openOrderGuideUpdateByPercenatge, setOpenOrderGuideUpdateByPercenatge] = useState(false);
  const [refetchOriginalPricing, setRefetchOriginalPricing] = useState(false)

  useEffect(() => {
    if (editOrderGuide && open) {
      setLoadingOrderGuideProducts(true);
      axios.get(`/orderGuide/orderGuideProducts?id=${editOrderGuide.id}`)
        .then(res => setOriginalOrderGuide(res.data ?? []))
        .catch(() => enqueueSnackbar('Get order guide products data failed', { variant: 'error' }))
        .finally(() => setLoadingOrderGuideProducts(false));
    } else {
      setOriginalOrderGuide([]);
      setShowPriceConfirmWarning(false);
    }
  }, [open, refetchOriginalPricing])

  useEffect(() => {
    const localProductsMap = products.filter(prod => prod.productCategoryId === editOrderGuide?.productCategoryId).map(product => {
      const oldOrderGuideProduct = originalOrderGuide.find(orderGuideItem => orderGuideItem.productUOMId === product.id);
      return {
        ...product,
        newPrice: oldOrderGuideProduct?.price,
        active: !!oldOrderGuideProduct
      };
    });
    setLocalProducts(localProductsMap.sort((prod, nexProd) => nexProd.active - prod.active));
  }, [products, originalOrderGuide, editOrderGuide]);

  const onSubmit = (igorePriceWarning) => {
    if (!igorePriceWarning && localProducts.some(product => product.newPrice !== null && product.newPrice !== undefined && product.price > product.newPrice))
    {
      setShowPriceConfirmWarning(true);
      return;
    }
    setShowPriceConfirmWarning(false);
    setSubmitting(true);
    axios.post("/orderGuide/orderGuideProductsForm", objectToFormData({
      orderGuideProducts: localProducts.filter(prod => prod.active).map(prod => ({
        price: prod.newPrice ?? prod.price,
        productUOMId: prod.id
      })),
      orderGuideId: editOrderGuide.id
    }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(`Order guide products updated`, { variant: 'success' });
          res.data.purchasingGroupName = purchasingGroups.find(purchasingGroup => purchasingGroup.value === res.data.purchasingGroupId)?.label;
          res.data.productCategoryName = productCategories.find(productCategory => productCategory.value === res.data.productCategoryId)?.label;
          res.data.facilityName = facilities.find(facility => facility.value === res.data.facilityId)?.label;
          setOrderGuides(orderGuides => [...orderGuides.filter(orderGuide => orderGuide.id !== editOrderGuide.id), res.data]);
          close();
        }
        else enqueueSnackbar(`Update order guide products failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`Update order guide products failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  const updateRow = (params, field, newValue) => {
    setLocalProducts(locProducts => {
      const oldProduct = locProducts.find(prod => prod.id === params.data.id);
      if (!oldProduct) return locProducts;
      oldProduct[field] = newValue;
      return locProducts;
    });
    const newNodeData = { ...params.node.data };
    newNodeData[field] = newValue
    params.node.setData(newNodeData);
    if (field === "active") params.api.refreshHeader();
  }

  const updateAllRowActives = (params, active) => {
    const updateRows = [];
    params.api.forEachNodeAfterFilter(node => {
      node.data.active = !active;
      updateRows.push(node.data.id);
    });
    setLocalProducts(locProducts => {
      locProducts.filter(prod => updateRows.includes(prod.id)).forEach(prod => { prod.active = !active; });
      return locProducts;
    });
    params.api.refreshCells();
    params.api.refreshHeader();
  }

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Product',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'productId',
      headerName: 'NHI Id',
      width: 150,
    },
    {
      field: 'vendorItemId',
      headerName: 'SKU',
      width: 150,
    },
    {
      field: 'uomType',
      width: 100,
    },
    {
      field: 'price',
      minWidth: 200,
      width: 200,
      suppressKeyboardEvent: (params) => true,
      valueGetter: (params) => params.data.newPrice ?? params.data.price ?? 0,
      cellRenderer: (params) => <Box sx={{ width: 168, mr: -2.5 }}>
        {(params.data.newPrice !== null && params.data.newPrice !== undefined && params.data.price !== params.data.newPrice) ? <Tooltip title={`Normal price: ${(params.data.price ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
          arrow placement="top">
          <IconButton edge="end" color="secondary" onClick={() => updateRow(params, "newPrice", params.data.price ?? 0)} disableRipple sx={{ px: 0 }}>
            <RestoreIcon />
          </IconButton>
        </Tooltip> : ""}
        <CurrencyInput
          name="price"
          size="small"
          placeholder="Please enter a price"
          sx={(params.data.newPrice !== null && params.data.newPrice !== undefined && params.data.price !== params.data.newPrice) ? {
            "& .MuiOutlinedInput-root": { height: "34px" },
            marginTop: "3px",
            ml: 2,
            width: "120px"
          } : { "& .MuiOutlinedInput-root": { height: "34px", width: "148px" }, marginTop: "3px" }}
          customInput={TextField}
          value={params.data.newPrice ?? params.data.price ?? 0.00}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          decimalScale={2}
          onValueChange={(value) => {
            if (!value || (value !== params.data.newPrice && !Number.isNaN(Number(value)))) updateRow(params, "newPrice", !value ? 0 : value);
          }}
        />
      </Box>
    },
    {
      field: 'active',
      width: 155,
      cellRenderer: (params) => <Checkbox checked={params.data.active ?? false} onChange={(e) => updateRow(params, "active", e.target.checked)} />,
      headerComponent: (params) => AGGridHeaderCheckboxSimple(params, updateAllRowActives),
    }
  ], []);

  return <Dialog open={open} onClose={close} maxWidth="xl" fullWidth>
    <DialogContent>
      <Stack spacing={1}>
        {editOrderGuide && <Typography variant="h3" textAlign="center">{editOrderGuide.facilityName || editOrderGuide.purchasingGroupName} | {editOrderGuide.productCategoryName}</Typography>}
        {gridRef?.current && <AGGridQuickFilterWrapper gridRef={gridRef}>
          <Button variant="contained" startIcon={<Iconify icon="tabler:percentage" />} onClick={() => { setOpenOrderGuideUpdateByPercenatge(true);  }} color="primary" size="small">
            <Typography>Pricing Update</Typography>
          </Button>
        </AGGridQuickFilterWrapper>
        }
        <Box>
          {(!loadingOrderGuideProducts && !loadingProducts) ? <Box sx={{ height: "650px" }}>
            <AgGridReact
              ref={gridRef}
              {...AgGridDefaults}
              columnDefs={[...columns]}
              rowData={localProducts}
              onFilterChanged={(params) => params.api.refreshHeader()}
            />
          </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
        </Box>
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained"
            onClick={() => onSubmit(false)}>
            Save
          </LoadingButton>
        </Box>
      </Stack>
      {showPriceConfirmWarning && <ConfirmDialog
        action={() => onSubmit(true)}
        cancelAction={() => setShowPriceConfirmWarning(false)}
        dialogOnly
        actionButton="Save"
        color="primary"
        title="This order guide has items with prices less than the vendor's cost price."
        content={ <Box>
          {localProducts.filter(product => product.newPrice !== null && product.newPrice !== undefined && product.price > product.newPrice)
            .map(product => <Typography key={product.Id} variant="subtitle1" textAlign="center">
              Product: {product.name} | Vendor Price: {product.price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} | New Price: {product.newPrice
                .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </Typography>)}
        </Box>}
      />}
      <OrderGuideUpdateByPercenatge
        selectedOrderGuide={editOrderGuide}
        open={openOrderGuideUpdateByPercenatge}
        close={() => { setOpenOrderGuideUpdateByPercenatge(false); setRefetchOriginalPricing(prev => !prev); }}
        products={products}
        purchasingGroups={purchasingGroups}
        productCategories={productCategories}
        facilities={facilities}
        loadingProducts={loadingProducts}
      />
    </DialogContent>
  </Dialog>;
}
